// @<COPYRIGHT>@
// ==================================================
// Copyright (c) 2020 Siemens
// Siemens Product Lifecycle Management Software Inc.
// All Rights Reserved.
// ==================================================
// @<COPYRIGHT>@

/**
 * This module provides workspace table actions.
 *
 * @module js/swf3dWorkspaceTableActions
 */

import _ from 'lodash';
import eventBus from 'js/eventBus';
import uwPropertySvc from 'js/uwPropertyService';
import AwFlexRow from 'viewmodel/AwFlexRowViewModel';
import AwFlexColumn from 'viewmodel/AwFlexColumnViewModel';
import AwToolbar from 'viewmodel/AwToolbarViewModel';
import AwSplmTable from 'viewmodel/AwSplmTableViewModel';
import * as swf3dTcxWorkspace from 'js/swf3dTcxWorkspace';
import Swf3dBreadcrumb from 'viewmodel/Swf3dWorkspaceBreadcrumbViewModel';
import appCtxSvc from 'js/appCtxService';
import msgSvc from 'js/messagingService';
import config from 'js/swf3dWorkspaceConfig';
import utils from 'js/swf3dWorkspaceUtils';
import input from 'js/swf3dWorkspaceInput';
import preview from 'js/swf3dWorkspacePreview';
import swf3dWorkspace from 'js/swf3dWorkspace';
import { delegator, pubSub, http } from '@swf3d/utils';
import { AwEnterKey } from 'js/hocCollection';
import AwButton from 'viewmodel/AwButtonViewModel';
import AwPanelSection from 'viewmodel/AwPanelSectionViewModel';
import AwTextbox from 'viewmodel/AwTextboxViewModel';
import AwProgressIndicator from 'viewmodel/AwProgressIndicatorViewModel';
import { getSelectedObjectCB, getCheckRowDataShowCB, getGenTableCB } from 'js/swf3dWorkspaceCallbacks';

const AwTextboxEnterKey = AwEnterKey(AwTextbox);
let swf3dTableCurrentFolderUrn = '';

const workspaceEventBusKey = 'galaxy.workspace.';

const _cellImageWidth = 65;
const _cellImageHeight = 65;

/**
 * Init function for setup workspace.
 * This function will run automatically before render the workspace dialog.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function initWorkspace
 * @param storage
 * @param tenantId
 * @param options
 * @param tenantid
 * @param url
 * @example swf3dWorkspace.initWorkspace( 'collab', 'tenantId', {URL: {collab: 'https://collab.us-east-1.sws.siemens.com', workspace:'/workspaceProxyUrl'}});
 */
export const initWorkspace = function (storage, tenantid, url, options) {
    if (swf3dWorkspace.isInitialized) {
        return;
    }
    if (utils.isPresent(storage)) {
        swf3dWorkspace.init({ URL: url });
        swf3dWorkspace.setShowPreview(false);

        if (storage === 'collab') {
            let subscribeKey = pubSub.subscribe('galaxyWorkspaceError', err => {
                let tip = '';
                if (err && /^5.+/.test(err.status)) {
                    tip = 'The connection to the server has been interrupted.';
                }
                if (err && /^4.+/.test(err.status)) {
                    tip = err.response || '';
                }
                if (tip) {
                    msgSvc.showError(tip);
                }
                pubSub.unSubscribe(subscribeKey);
            });
            swf3dWorkspace.collab();
        } else if (storage === 'tc') {
            let tcReady = appCtxSvc.getCtx('galaxy.delegator.ready');
            if (tcReady === 'false' && options && options.setupTC) {
                options.setupTC();
            }
            swf3dWorkspace.TC({ tenantId: tenantid });
        } else {
            swf3dWorkspace.local(storage);
        }
    }
};

/**
 * Default render function for table
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function render
 * @param {string} type
 * @param {boolean} isTcx
 * @param {string} tenantId
 * @param {object} url
 * @param {object} grids
 * @example
 * swf3dWorkspaceTableActions.render({});
 */
export const render = ({ grids, type, tenantid, url, options, isTcx = false, i18n = {}, fields: { searchPartUrn }, actions: { searchPart } }) => {
    if (!isTcx) {
        initWorkspace(type, tenantid, url, options);
    }

    const gridId = isTcx ? 'tcxPartListTable' : 'swf3dWorkspaceTable';
    const tableProps = isTcx ? grids.tcxPartListTable : grids.swf3dWorkspaceTable;

    return (
        <AwFlexColumn width='fill'>
            <AwPanelSection caption={i18n.searchTitle || ''}>
                <div>
                    <AwTextboxEnterKey {...searchPartUrn} awEnterKey={searchPart}></AwTextboxEnterKey>
                    <AwButton class='small' button-type='base' action={searchPart}>
                        {i18n.searchPartTitle || ''}
                    </AwButton>
                </div>
            </AwPanelSection>
            {isTcx ? <AwProgressIndicator name='tcxDataIndicator'></AwProgressIndicator> : null}
            {!isTcx ? <AwProgressIndicator name='collabDataIndicator'></AwProgressIndicator> : null}

            <AwFlexRow height='2f'>
                <AwFlexColumn width='10'>
                    <Swf3dBreadcrumb />
                </AwFlexColumn>
                <AwFlexColumn width='2'>
                    <AwToolbar firstAnchor='' secondAnchor='workspaceToolbar'></AwToolbar>
                </AwFlexColumn>
            </AwFlexRow>
            <AwFlexRow height='fill' className='swf3d-workspace-table'>
                <AwSplmTable gridid={gridId} {...tableProps}></AwSplmTable>
            </AwFlexRow>
            <AwFlexRow height='1f'></AwFlexRow>
        </AwFlexColumn>
    );
};

/**
 * Reset current folder urn.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function resetCurrentFolderUrn
 * @param {string} urn - table current folder urn
 * @example
 * swf3dWorkspaceTableActions.resetCurrentFolderUrn(urn);
 */
export const resetCurrentFolderUrn = urn => {
    appCtxSvc.updatePartialCtx('galaxy.workspace.tableCurrentFolderUrn', urn);
};

/**
 * Initial workspace view
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function setViewAndRegisterUrn
 * @param {string} name - CTX name
 * @param {string} value - CTX value
 * @example
 * swf3dWorkspaceTableActions.setViewAndRegisterUrn(name, value);
 */
export const setViewAndRegisterUrn = (name, value) => {
    if (!appCtxSvc.getCtx('galaxy.workspace.mode')) {
        appCtxSvc.updatePartialCtx('galaxy.workspace.mode', 'table');
    }
    appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects', {
        count: 0,
        objects: []
    });
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.searchText', '');
    if (!utils.isPresent(appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn'))) {
        appCtxSvc.updatePartialCtx(name, value);
    }
};

/**
 * Apollo usage: load workspace table column.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function loadColumnsAndAddCellRenderer
 * @param {object} columnProvider - Apollo column provider
 * @param {object} dataProvider - Apollo data provider
 * @example
 * swf3dWorkspaceTableActions.loadColumnsAndAddCellRenderer(columnProvider, dataProvider);
 * @returns {object} - column info
 */
export let loadColumnsAndAddCellRenderer = (columnProvider, dataProvider) => {
    columnProvider.columns[0].cellRenderers = [
        {
            action: function (column, vmo) {
                let cell = document.createElement('div');

                let cellImage = document.createElement('img');
                cellImage.setAttribute('id', 'swf3d-table-image-cell-' + vmo.id);
                // Set up image size.
                cellImage.width = _cellImageWidth;
                cellImage.height = _cellImageHeight;
                cell.appendChild(cellImage);

                preview.getPreviewImage(vmo.props.type.uiValue, vmo.props.object.dbValue[0]).then(image => {
                    let folderObjects = appCtxSvc.getCtx('galaxy.workspace.cacheFolderObjects');
                    if (folderObjects && folderObjects.objects && folderObjects.objects[vmo.id]) {
                        folderObjects.objects[vmo.id].image = image;
                        appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects', folderObjects);
                    }
                    cellImage.src = image;
                    /**
                     * Cannot show in async now, this is Shaishav's solution, but it does not work.
                     */
                });

                return cell;
            },
            condition: function (column) {
                return column.name === 'object';
            }
        }
    ];

    dataProvider.columnConfig = {
        columns: columnProvider.columns
    };

    return {
        columnInfos: columnProvider.columns
    };
};

/**
 * Commonm method for get table input data.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function getTableInputData
 * @param {object} data - workspace table input data
 * @example
 * swf3dWorkspaceTableActions.getTableInputData(data);
 * @returns {object} - workspace input data
 */
function getTableInputData(data) {
    let sortDirection = null;
    let sortColName = null;
    let limit = 50;
    if (data.columnProviders.Swf3dColumnProvider.sortCriteria.length > 0) {
        sortDirection = data.columnProviders.Swf3dColumnProvider.sortCriteria[0].sortDirection;
        sortColName = data.columnProviders.Swf3dColumnProvider.sortCriteria[0].fieldName;
        if (checkIfTableSorting(data)) {
            limit = data.dataProviders.Swf3dDataProvider.viewModelCollection.totalObjectsLoaded;
        }
    }

    let filterList = appCtxSvc.getCtx('galaxy.workspace.table.currentFilterList');
    let inputData = {
        urn: appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn'),
        getAllChildren: false,
        startIndex: data.dataProviders.Swf3dDataProvider.startIndex,
        limit: limit,
        sortDirection: sortDirection,
        sortColName: sortColName,
        filterList: filterList,
        appInfo: config.appInfo,
        WSAuthorization: config.authorization
    };

    return inputData;
}

/**
 * Method for get table input data.
 * When collab mode.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function getCollabInputData
 * @example
 * swf3dWorkspaceTableActions.getCollabInputData();
 * @returns {object} - Collab input data
 */
function getCollabInputData() {
    return {
        collabSpaceId: config.collabSpaceId,
        projectId: config.projectId,
        COLLAB: config.collabHost,
        DSS: config.dssHost
    };
}

/**
 * Build request payload for the collab search endpoint.
 *
 * @param {object} data - View model data.
 * @param {string} keyword - Search keyword.
 * @returns {{url: string, payload: object}} Search request info.
 */
function buildCollabSearchRequest(data, keyword) {
    const tableInput = getTableInputData(data);
    const inputData = {
        keyword,
        collabSpaceId: config.collabSpaceId,
        projectId: config.projectId,
        COLLAB: config.collabHost,
        DSS: config.dssHost,
        startIndex: tableInput.startIndex || 0,
        limit: tableInput.limit || 50
    };
    return {
        url: config.url + '/collab/search',
        inputData
    };
}

/**
 * Get input data
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function getInputData
 * @param {object} data - input data
 * @example
 * swf3dWorkspaceTableActions.getInputData(data);
 * @returns {object} - custom input data with other data in config file
 */
function getInputData(data) {
    let commonInputData = getTableInputData(data);
    let inputData = {};
    if (config.storage === 'collab') {
        inputData = getCollabInputData();
    } else if (config.storage === 'tc') {
        inputData = input.getTCInputData();
    }
    return { ...commonInputData, ...inputData };
}

/**
 * Get workspace data from workspace service.
 * Then handle response data.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function loadData
 * @param {object} data - data in view model
 * @example
 * swf3dWorkspaceTableActions.loadData(data);
 * @returns {object} - workspace list
 */
export const loadData = data => {
    return config.ready.then(authRes => {
        const qRaw = appCtxSvc.getCtx('galaxy.workspace.table.searchText');
        const q = (qRaw || '').toString().trim();

        if (config.storage === 'collab' && q) {
            const { url, inputData } = buildCollabSearchRequest(data, q);
            eventBus.publish('collabDataIndicator-progress.start');
            return http
                .get(url, { data: JSON.stringify(inputData) })
                .then(response => {
                    // Discard response if the search query changed while the request was in flight.
                    const currentQ = (appCtxSvc.getCtx('galaxy.workspace.table.searchText') || '').toString().trim();
                    if (currentQ !== q) {
                        return { rowsLoaded: [], totalFound: 0, searchResults: [], nextSearchIndex: -1 };
                    }
                    const startIndex = inputData.startIndex || 0;
                    const _columnInfos = data.columnProviders.Swf3dColumnProvider.columns;
                    // Populate cacheFolderObjects so the cell renderer can resolve icons.
                    appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects', response);
                    const vmRows = generateApolloTableRows(_columnInfos, response.objects || [], response.count || 0, startIndex);
                    return {
                        rowsLoaded: [],
                        totalFound: vmRows.rowCount,
                        searchResults: vmRows.uiResults,
                        nextSearchIndex: -1
                    };
                })
                .catch(function (error) {
                    return {};
                })
                .finally(() => {
                    eventBus.publish('collabDataIndicator-progress.end');
                });
        }

        // Fetch from service.
        const requestFolderUrn = appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn');
        let url = config.url + '/' + config.storage + '/' + config.resourceType;
        let inputData = getInputData(data);
        return http
            .get(url, { data: JSON.stringify(inputData) })
            .then(response => {
                // Discard stale response if the user navigated to a different folder while the request was in flight.
                if (config.storage === 'collab' && appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn') !== requestFolderUrn) {
                    return {
                        rowsLoaded: [],
                        totalFound: 0,
                        searchResults: [],
                        nextSearchIndex: -1
                    };
                }

                updateWorkspaceCtx(data, response);
                const _columnInfos = data.columnProviders.Swf3dColumnProvider.columns;
                const startIndex = data.dataProviders.Swf3dDataProvider.startIndex;
                const vmRows = generateApolloTableRows(_columnInfos, response.objects, response.count, startIndex);
                return {
                    rowsLoaded: [],
                    totalFound: vmRows.rowCount,
                    searchResults: vmRows.uiResults,
                    nextSearchIndex: -1
                };
            })
            .catch(function (error) {
                return {};
            });
    });
};

/**
 * After data return from service, it needs to change to the structure that Apollo define.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function generateApolloTableRows
 * @param {object} _columnInfos - Apollo column provider info
 * @param {object} responseObjects - row data
 * @param {number} currentCount - current count
 * @param {number} startIndex - start row index
 * @example
 * swf3dWorkspaceTableActions.generateApolloTableRows(_columnInfos, responseObjects, currentCount, startInde);
 * @returns {object} - table row data with right page size
 */
function generateApolloTableRows(_columnInfos, responseObjects, currentCount, startIndex) {
    let uiRowsObjects = [];
    let index = startIndex;
    let checkRowData = getCheckRowDataShowCB();
    let genTableCB = getGenTableCB();
    responseObjects.forEach(eData => {
        if (checkRowData(eData)) {
            const ViewModelRow = function (rowId) {
                this.id = rowId;
                this.props = {};
            };
            let vmRow = new ViewModelRow(index);
            vmRow.projectId = eData.projectId;
            _.forEach(_columnInfos, function (column) {
                let colData = genTableCB(column.name, eData);
                let vmProp = uwPropertySvc.createViewModelProperty(column.name, column.displayName, colData.type, colData.dbValues, colData.uiValues);

                vmProp.propertyDescriptor = {
                    displayName: column.displayName
                };
                vmRow.props[column.name] = vmProp;
            });
            index++;
            vmRow.uid = index;
            uiRowsObjects.push(vmRow);
        } else {
            currentCount--;
        }
    });
    return {
        uiResults: uiRowsObjects,
        rowCount: currentCount
    };
}

/**
 * Check if the function is loaded when user want to sort.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function checkIfTableSorting
 * @param {object} data - The data in workspace view model
 * @example
 * swf3dWorkspaceTableActions.checkIfTableSorting(data);
 * @returns {boolean} - if table need sorting
 */
function checkIfTableSorting(data) {
    let isSorting = false;
    let sortCriteria = data.columnProviders.Swf3dColumnProvider.sortCriteria;
    let vmCollection = data.dataProviders.Swf3dDataProvider.getViewModelCollection();
    let totalObjectsLoaded = vmCollection.totalObjectsLoaded;
    let startIndex = data.dataProviders.Swf3dDataProvider.startIndex;
    if (sortCriteria && sortCriteria.length > 0) {
        // if it has sorting attribute
        if (totalObjectsLoaded !== 0) {
            // it has already loaded
            if (startIndex !== 0) {
                // it wants to load more object
                isSorting = false;
            } else {
                // if it is sorting, the start index will be 0
                isSorting = true;
            }
        } else {
            // it has not loaded
            /* istanbul ignore next */
            isSorting = false;
        }
    } else {
        // if it does not have sorting attribute
        if (totalObjectsLoaded !== 0) {
            // it has already loaded
            if (startIndex !== 0) {
                // it wants to load more object
                isSorting = false;
            } else {
                // if it is sorting, the start index will be 0
                isSorting = true;
            }
        } else {
            /* istanbul ignore next */
            isSorting = false;
        }
    }
    return isSorting;
}

/**
 * Update the workspace data in ctx for grid mode
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function updateWorkspaceCtx
 * @param {object} data - The data in workspace view model
 * @param {object} response - response from workspace service
 * @example
 * swf3dWorkspaceTableActions.updateWorkspaceCtx(data);
 */
function updateWorkspaceCtx(data, response) {
    let allData = {
        count: 0,
        objects: []
    };
    if (swf3dTableCurrentFolderUrn !== appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn') || checkIfTableSorting(data)) {
        swf3dTableCurrentFolderUrn = appCtxSvc.getCtx('galaxy.workspace.tableCurrentFolderUrn');
        appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects', response);
    } else {
        if (appCtxSvc.getCtx('galaxy.workspace.cacheFolderObjects')) {
            allData = appCtxSvc.getCtx('galaxy.workspace.cacheFolderObjects');
        }
        allData.count = response.count;
        allData.objects = allData.objects.concat(response.objects);
        appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects', allData);
    }
}

/**
 * Reset workspace data when user opens another folder.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function resetWorkspaceData
 * @param {object} data - The data in workspace view model
 * @example
 * swf3dWorkspaceTableActions.resetWorkspaceData(data);
 */
export const resetWorkspaceData = data => {
    let _dataprovider = data.dataProviders.Swf3dDataProvider;
    let vmCollection = _dataprovider.getViewModelCollection();
    vmCollection.clear();
    appCtxSvc.updatePartialCtx('galaxy.workspace.cacheFolderObjects.objects', []);
    eventBus.publish('swf3dWorkspaceTable.plTable.reload');
};

/**
 * Do action when user clicks folder or part.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function selectionChangedAction
 * @param {object} selected - Selected row item
 * @param {object} viewModelData - The data in workspace view model
 * @example
 * swf3dWorkspaceTableActions.selectionChangedAction(selected, viewModelData);
 */
export const selectionChangedAction = (selected, viewModelData) => {
    if (utils.isPresent(selected)) {
        if (utils.isPresent(selected.props)) {
            // table mode
            const data = {
                urn: selected.props.object.dbValue[0],
                name: selected.props.name.dbValue[0],
                type: selected.props.type.dbValue[0],
                date: selected.props.date.dbValue[0],
                projectId: selected.projectId
            };
            selectExecutor(data, viewModelData);
        } else {
            // grid mode
            selectExecutor(selected, viewModelData);
        }
    }
};

/**
 * Select excutor.
 * For publish event to outside.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function selectExecutor
 * @param {object} data - Selected row item
 * @param {object} viewModelData - The data in workspace view model
 * @example
 * swf3dWorkspaceTableActions.selectExecutor({type: 'Project'}, viewModelData);
 */
function selectExecutor(data, viewModelData) {
    if (data.type === 'Project') {
        // Project
        clickProject(data);
        eventBus.publish(workspaceEventBusKey + 'onProjectSelected', { ...data, collaborationSpaceId: config.collabSpaceId });
        let projects = appCtxSvc.getCtx('galaxy.workspace.cacheFolderObjects');
        let project = projects?.objects.find(i => data.urn === i.urn);
        appCtxSvc.registerCtx('galaxy.workspace.currentProject', project);
    } else if (data.type === 'Folder') {
        // folder
        clickFolder(data);
        eventBus.publish(workspaceEventBusKey + 'onFolderSelected', data);
    } else {
        // part
        appCtxSvc.updatePartialCtx('galaxy.workspace.selectedObject', data);
        if (utils.checkType(data.type)) {
            let selectedObjectCB = getSelectedObjectCB();
            if (utils.isPresent(selectedObjectCB)) {
                data = selectedObjectCB(data, viewModelData);
            }

            // Add collab data.
            data.collaborationSpaceId = config.collabSpaceId;
            data.containerId = data.projectId || config.projectId;
            data.containerType = 'project';
            data.region = 'us-east-1.preprod.teamcenterwebservices.com';

            // If coming from search without a project context, set the delegator context first
            if (!config.projectId && data.projectId) {
                delegator.API.switchProject(config.collabSpaceId, data.projectId, 'project');
            }

            eventBus.publish(workspaceEventBusKey + 'onPartSelected', data);
        }
    }
}

/**
 * Click folder.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function clickFolder
 * @param {object} data - Selected row item
 * @example
 * swf3dWorkspaceTableActions.clickFolder({urn: 'fakeUrn'});
 */
const clickFolder = function (data) {
    config.resourceType = 'objects';
    resetCurrentFolderUrn(data.urn);
    // reset search keyword when click folder
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.searchText', '');
    let running = appCtxSvc.getCtx('tableDataLoadingStatus');
    if (running) {
        return;
    }
    appCtxSvc.registerCtx('tableDataLoadingStatus', true);
    eventBus.publish('galaxy.workspace.resetTable');
    addNewBreadcrumb(data.name, data.urn);
};

/**
 * Click project.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function clickProject
 * @param {object} data - Selected row item
 * @example
 * swf3dWorkspaceTableActions.clickProject({urn: 'fakeUrn'});
 */
const clickProject = function (data) {
    delegator.API.switchProject(config.collabSpaceId, data.urn, 'project');
    config.resourceType = 'objects';
    config.projectId = data.urn;
    resetCurrentFolderUrn('');
    // reset search keyword when open project
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.searchText', '');
    let running = appCtxSvc.getCtx('tableDataLoadingStatus');
    if (running) {
        return;
    }
    appCtxSvc.registerCtx('tableDataLoadingStatus', true);
    eventBus.publish('galaxy.workspace.resetTable');
    addNewBreadcrumb(data.name, '');
    appCtxSvc.registerCtx('galaxy.workspace.currentProject', data);
};

/**
 * Add a new node to bread crumb when user clicks folder.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function addNewBreadcrumb
 * @param {string} folderName Folder name
 * @param {string} folderUrn Folder path
 * @example
 * swf3dWorkspaceTableActions.addNewBreadcrumb('test','folderUrn');
 */
function addNewBreadcrumb(folderName, folderUrn) {
    const breadcrumbData = {
        displayName: folderName,
        urn: folderUrn
    };
    eventBus.publish('galaxy.workspace.newBreadcrumb', breadcrumbData);
}

/**
 * When entering a folder, it needs to load the next folder data.
 * When it loads success, make the breadcrumb enable.
 *
 * @memberof js/swf3dWorkspaceTableActions
 * @function swf3dWorkspaceLoadDataSuccess
 * @param {object} data - The data in workspace view model
 * @example
 * swf3dWorkspaceTableActions.swf3dWorkspaceLoadDataSuccess(data);
 */
export const swf3dWorkspaceLoadDataSuccess = data => {
    appCtxSvc.unRegisterCtx('tableDataLoadingStatus');
    const selectionModel = data.dataProviders.Swf3dDataProvider.selectionModel;
    selectionModel.selectNone();

    appCtxSvc.updatePartialCtx('galaxy.workspace.loadMoreProcessing', false);
};

/**
 * search entry: For TCX, use the tc search;
 * for Collab, update the filter and trigger the loading.
 *
 * @param {string} qString - Search keywords (TCX represents the component ID; Collab represents the name/key word)
 * @param {object} dataProvider - View model's data provider
 * @param {boolean} isTcx - if current environment is TCX
 * @returns {Promise<any>} Search promise (resolved for TCX or collab).
 */
export function searchPart(qString, dataProvider, isTcx) {
    if (isTcx) {
        return Promise.resolve(swf3dTcxWorkspace.searchPart(qString, dataProvider));
    }
    // Collab search
    const q = (qString && String(qString).trim()) || '';
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.searchText', q);
    eventBus.publish('galaxy.workspace.resetTable');
    eventBus.publish('galaxy.workspace.loadData');
    return Promise.resolve();
}

export default {
    resetCurrentFolderUrn,
    setViewAndRegisterUrn,
    loadColumnsAndAddCellRenderer,
    loadData,
    resetWorkspaceData,
    selectionChangedAction,
    swf3dWorkspaceLoadDataSuccess,
    searchPart
};
